import { assert, assertNever, defined, definedMap, proveNever } from "@glideapps/ts-necessities";
import { iterableEnumerate } from "collection-utils";
import entries from "lodash/entries";

import { AppDescription, getAppTabs } from "../../components/SerializedApp";
import { TableName } from "../../database-strings";
import {
    ActionsRecord,
    ComponentConfiguratorContext,
    ComponentDescriptor,
    EditedColumns,
    isNamedPropertySource,
    PropertyDescriptor,
    PropertyDescriptorCase,
} from ".";
import {
    ActionDescription,
    ActionKind,
    addClassScreenName,
    arrayScreenName,
    ArrayTransform,
    ArrayTransformKind,
    classScreenName,
    ColumnAssignment,
    ComponentDescription,
    Description,
    editClassScreenName,
    EditScreenDescription,
    Formula,
    getScreenComponents,
    getTableName,
    InputOutputTables,
    isClassOrArrayScreenDescription,
    isDefaultArrayScreenName,
    isFreeScreen,
    makeInputOutputTables,
    makeTableRef,
    MutatingScreenKind,
    mutatingScreenKindForDescription,
    PropertyDescription,
    PropertyKind,
    ScreenDescription,
    ScreenDescriptionKind,
    TableGlideType,
    userProfileScreenName,
} from "../description";
import { PropertySection } from "./property-sections";
import {
    getActionProperty,
    getArrayProperty,
    getCompoundActionProperty,
    getScreenProperty,
    getSwitchProperty,
    isPropertyDescription,
} from "./property-utils";
import { handlerForArrayScreenFormat } from "../array-screens";
import { handlerForActionKind } from "../actions";
import {
    getColumnAssignments,
    getPropertyAndDescriptorFromDescription,
    inputOutputTablesForClassOrArrayScreen,
    makeInputOutputTablesForFormOnSubmitAction,
} from "../description-utils";
import { handlerForComponentKind } from "../handlers";
import { ActionNodeKind, CompoundActionNode, walkActionNode } from "../actions/compound";
import { getUserProfileDescriptors } from "../user-profile";
import { isArray } from "../../Support";
import { ComponentIndexes } from "../../component-indexes";
import { getSubComponents } from "../sub-components";

export enum LocationKind {
    Tab,
    Screen,
    Component,
    ScreenColumnAssignment,
    Action,
    UserProfile,
}

interface TabLocation {
    readonly kind: LocationKind.Tab;
    readonly index: number;
}

interface ScreenLocation {
    readonly kind: LocationKind.Screen;
    readonly screenName: string;
    readonly tabIndex: number | undefined;
    readonly propertySection: PropertySection | undefined;
    readonly isWritten: boolean;
}

interface ComponentLocation {
    readonly kind: LocationKind.Component;
    readonly screenName: string;
    readonly tabIndex: number | undefined;
    readonly componentIndexes: ComponentIndexes;
    readonly componentDisplayName: string;
    readonly isWritten: boolean;
}

interface ScreenColumnAssignmentLocation {
    readonly kind: LocationKind.ScreenColumnAssignment;
    readonly screenName: string;
    readonly tabIndex: number | undefined;
    readonly tableName: TableName;
    readonly columnName: string;
    readonly assignmentIndex: number;
}

interface ActionLocation {
    readonly kind: LocationKind.Action;
    readonly actionID: string;
    readonly nodeKey: string;
    readonly actionDisplayName: string | undefined;
    readonly isWritten: boolean;
}

interface UserProfileLocation {
    readonly kind: LocationKind.UserProfile;
    readonly name: string;
    readonly isWritten: boolean;
}

export type Location =
    | TabLocation
    | ScreenLocation
    | ComponentLocation
    | ScreenColumnAssignmentLocation
    | ActionLocation
    | UserProfileLocation;

export function getScreenNameForLocation(l: Location): string | undefined {
    switch (l.kind) {
        case LocationKind.Screen:
        case LocationKind.Component:
        case LocationKind.ScreenColumnAssignment:
            return l.screenName;

        case LocationKind.Tab:
        case LocationKind.Action:
        case LocationKind.UserProfile:
            return undefined;

        default:
            return assertNever(l);
    }
}

export interface AppDescriptionVisitor {
    visitScreen?(screenName: string, screen: ScreenDescription, tables: InputOutputTables | undefined): void;
    // visitTabDescription(tab: TabDescription): void;
    visitFormula?(
        location: Location,
        formula: Formula,
        contextTableName: TableName | undefined,
        containingScreenTableName: TableName | undefined,
        propertyDescr: PropertyDescriptor | undefined
    ): void;
    visitProperty?(
        location: Location,
        desc: PropertyDescription,
        propertyDescr: PropertyDescriptor | undefined,
        pdc: PropertyDescriptorCase<PropertyDescription> | undefined,
        // The component/action/screen description containing this property
        containingDesc: Description,
        tables: InputOutputTables | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): void;
    // This might get invoked with duplicates
    visitColumnWritten?(location: Location, tableName: TableName, columnName: string): void;
}

function doesPDCWrite(pdc: PropertyDescriptorCase<PropertyDescription> | undefined): boolean {
    return pdc?.kind === PropertyKind.Column && pdc.isEditedInApp === true;
}

class AppWalker {
    private readonly builderActionsToVisit: [string, TableGlideType | undefined][] = [];
    private readonly screensToVisit: string[] = [];

    constructor(private readonly ccc: ComponentConfiguratorContext, private readonly visitor: AppDescriptionVisitor) {
        assert(ccc.appDescription !== undefined);
    }

    private get app(): AppDescription {
        return defined(this.ccc.appDescription);
    }

    private addScreensToVisit(screenNames: readonly string[]): void {
        for (const screenName of screenNames) {
            this.screensToVisit.push(screenName);
        }
    }

    private visitArrayTransforms(
        transforms: readonly ArrayTransform[] | undefined,
        contextTableName: TableName | undefined,
        containingScreenTableName: TableName | undefined,
        propertyDescr: PropertyDescriptor | undefined,
        makeLocation: (kind: ArrayTransformKind.Filter | ArrayTransformKind.Sort) => Location
    ): void {
        for (const transform of transforms ?? []) {
            switch (transform.kind) {
                case ArrayTransformKind.Filter:
                    this.visitor.visitFormula?.(
                        makeLocation(transform.kind),
                        transform.predicate,
                        contextTableName,
                        containingScreenTableName,
                        propertyDescr
                    );
                    break;
                case ArrayTransformKind.Sort:
                    for (const key of transform.keys) {
                        this.visitor.visitFormula?.(
                            makeLocation(transform.kind),
                            key.key,
                            contextTableName,
                            containingScreenTableName,
                            propertyDescr
                        );
                    }
                    break;
                // Here's where we have to add the property `numRows` for
                // ##limitTransformInGlobalSearch.
                case ArrayTransformKind.Shuffle:
                case ArrayTransformKind.TableOrder:
                case ArrayTransformKind.Limit:
                    break;
                default:
                    return proveNever(transform, "Unknown array transform", undefined);
            }
        }
    }

    private visitProperty(
        desc: PropertyDescription | undefined,
        propertyDescr: PropertyDescriptor | undefined,
        pdc: PropertyDescriptorCase<Description> | undefined,
        tables: InputOutputTables | undefined,
        containingDesc: Description,
        mutatingScreenKind: MutatingScreenKind | undefined,
        makeLocation: (
            pdc: PropertyDescriptorCase<Description> | undefined,
            propertyDescr: PropertyDescriptor | undefined
        ) => Location
    ): void {
        // FIXME: This whole thing should be in the description handler

        if (desc === undefined) return;

        this.visitor.visitProperty?.(
            makeLocation(pdc, propertyDescr),
            desc,
            propertyDescr,
            pdc,
            containingDesc,
            tables,
            mutatingScreenKind
        );

        if (!isPropertyDescription(desc)) return;

        if (desc.kind === PropertyKind.Transforms && (pdc === undefined || pdc.kind === PropertyKind.Transforms)) {
            const contextTable = definedMap(
                tables,
                t => pdc?.getIndirectTable?.(t, containingDesc, this.ccc) ?? t.input
            );
            const containingScreenTable = pdc?.withContainingScreen === true ? tables?.input : undefined;
            assert(isArray(desc.value));
            this.visitArrayTransforms(
                desc.value as readonly ArrayTransform[],
                definedMap(contextTable, getTableName),
                definedMap(containingScreenTable, getTableName),
                propertyDescr,
                () => makeLocation(pdc, propertyDescr)
            );
            return;
        }

        const action = getActionProperty(desc);
        if (action !== undefined) {
            this.visitAction(action, tables, mutatingScreenKind, makeLocation);
        }

        const actionID = getCompoundActionProperty(desc);
        if (actionID !== undefined) {
            this.builderActionsToVisit.push([actionID, tables?.input]);
            return;
        }

        const array = getArrayProperty<Description>(desc);
        if (array !== undefined && pdc?.kind === PropertyKind.Array) {
            for (const itemDesc of array) {
                this.visitProperties(
                    itemDesc,
                    containingDesc,
                    pdc.properties,
                    tables,
                    mutatingScreenKind,
                    makeLocation
                );
            }
        }
    }

    private visitProperties(
        desc: Description,
        containingDesc: Description,
        descriptors: readonly PropertyDescriptor[],
        tables: InputOutputTables | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        makeLocation: (
            pdc: PropertyDescriptorCase<Description> | undefined,
            propertyDescr: PropertyDescriptor | undefined
        ) => Location
    ): void {
        for (const propertyDescr of descriptors) {
            const propertyAndDescriptor = getPropertyAndDescriptorFromDescription(desc, propertyDescr);
            if (propertyAndDescriptor === undefined) continue;

            const { description: pd, descriptorCase: pdc } = propertyAndDescriptor;

            this.visitProperty(pd, propertyDescr, pdc, tables, containingDesc, mutatingScreenKind, makeLocation);
        }
    }

    private visitColumnEdits(edits: EditedColumns, location: Location): void {
        for (const [columnName, , tableName] of edits) {
            this.visitor.visitColumnWritten?.(location, tableName, columnName);
        }
    }

    private visitComponents(
        components: readonly ComponentDescription[],
        tables: InputOutputTables | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        parentIndex: readonly [] | readonly [number],
        makeLocation: (
            desc: ComponentDescription,
            index: ComponentIndexes,
            descr: ComponentDescriptor<ComponentDescription>,
            pdc: PropertyDescriptorCase<PropertyDescription> | undefined
        ) => Location
    ): void {
        const { ccc } = this;

        for (const [index, desc] of iterableEnumerate(components)) {
            const componentIndexes: ComponentIndexes = [...parentIndex, index];
            const handler = handlerForComponentKind(desc.kind);
            if (handler === undefined) continue;

            this.addScreensToVisit(handler.getScreensUsed(desc, ccc, tables));

            const descr = handler.getDescriptor(desc, tables?.input, ccc, mutatingScreenKind, undefined, undefined);

            this.visitProperties(desc, desc, descr.properties, tables, mutatingScreenKind, pdc =>
                makeLocation(desc, componentIndexes, descr, pdc)
            );

            let actionTables: InputOutputTables | undefined;
            if (descr.getActionTables !== undefined) {
                if (tables !== undefined) {
                    actionTables = descr.getActionTables?.(tables, desc, ccc);
                }
            } else {
                actionTables = tables;
            }
            this.visitActionsRecord(
                handler.getActions(desc, actionTables?.input, ccc, mutatingScreenKind),
                actionTables,
                mutatingScreenKind,
                pdc => makeLocation(desc, componentIndexes, descr, pdc)
            );

            if (tables !== undefined) {
                const edits = handler.getEditedColumns(desc, tables, ccc, mutatingScreenKind, false);
                this.visitColumnEdits(edits, makeLocation(desc, componentIndexes, descr, undefined));
            }

            const subComponents = getSubComponents(desc);
            if (subComponents !== undefined) {
                assert(componentIndexes.length === 1);
                this.visitComponents(subComponents, tables, mutatingScreenKind, componentIndexes, makeLocation);
            }
        }
    }

    private visitColumnAssignments(
        columnAssignments: readonly ColumnAssignment[],
        tables: InputOutputTables,
        containingDesc: Description,
        mutatingScreenKind: MutatingScreenKind | undefined,
        makeLocation: (assignment: ColumnAssignment, index: number) => Location
    ): void {
        let x = 0;
        for (const assignment of columnAssignments) {
            const location = makeLocation(assignment, x);
            this.visitor.visitColumnWritten?.(location, getTableName(tables.output), assignment.destColumn);
            this.visitProperty(
                assignment.value,
                undefined,
                undefined,
                tables,
                containingDesc,
                mutatingScreenKind,
                () => location
            );
            x++;
        }
    }

    private visitAction(
        action: ActionDescription,
        tables: InputOutputTables | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        makeLocation: (
            pdc: PropertyDescriptorCase<Description> | undefined,
            propertyDescr: PropertyDescriptor | undefined
        ) => Location
    ): void {
        const { ccc } = this;

        const handler = handlerForActionKind(action.kind);
        if (handler === undefined) return;

        action = handler.getDescriptionToWalk(action, ccc);

        this.addScreensToVisit(handler.getScreensUsed(action, ccc, tables));

        const descr = handler.getDescriptor(action, tables, ccc, mutatingScreenKind);
        if (descr === undefined) return;

        this.visitProperties(action, action, descr.properties, tables, mutatingScreenKind, makeLocation);

        // All the rest below will be processed as part of the builder action
        // later.
        if (action.kind === ActionKind.Compound) return;

        if (tables !== undefined) {
            const assignments = handler.getColumnAssignments(ccc, action, tables);
            if (assignments !== undefined) {
                const location = makeLocation(undefined, undefined);
                this.visitColumnAssignments(
                    assignments.assignments,
                    makeInputOutputTables(tables.input, assignments.table),
                    action,
                    mutatingScreenKind,
                    () => location
                );
            }

            const edits = handler.getEditedColumns(action, tables, ccc, mutatingScreenKind);
            if (edits !== undefined) {
                this.visitColumnEdits(edits, makeLocation(undefined, undefined));
            }
        }
    }

    private visitActionsRecord(
        actionsRecord: ActionsRecord,
        tables: InputOutputTables | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        makeLocation: (pdc: PropertyDescriptorCase<PropertyDescription> | undefined, propertyName: string) => Location
    ): void {
        for (const [name, actions] of entries(actionsRecord)) {
            for (const action of actions) {
                this.visitAction(action, tables, mutatingScreenKind, pdc => makeLocation(pdc, name));
            }
        }
    }

    private visitScreen(screenName: string): void {
        const { ccc, app } = this;

        const screen = app.screenDescriptions[screenName];
        if (screen === undefined) return;

        let tables: InputOutputTables | undefined;
        if (isClassOrArrayScreenDescription(screen)) {
            tables = inputOutputTablesForClassOrArrayScreen(screen, this.ccc.schema);
        }

        this.visitor.visitScreen?.(screenName, screen, tables);

        if (!isClassOrArrayScreenDescription(screen)) return;

        const contextTableName = definedMap(tables?.input, getTableName);

        const mutatingScreenKind = mutatingScreenKindForDescription(screenName, screen);
        const tabs = getAppTabs(app);
        const maybeTabIndex = tabs.findIndex(t => getScreenProperty(t.screenName) === screenName);
        const tabIndex = maybeTabIndex >= 0 ? maybeTabIndex : undefined;

        const location = { kind: LocationKind.Screen, screenName, tabIndex } as const;

        this.visitArrayTransforms(screen.transforms, contextTableName, undefined, undefined, kind => ({
            ...location,
            propertySection: kind === ArrayTransformKind.Filter ? PropertySection.FilterData : PropertySection.Sort,
            isWritten: false,
        }));

        const visitScreenComponents = (): void => {
            assert(isClassOrArrayScreenDescription(screen));
            const components = getScreenComponents(screen);
            this.visitComponents(components, tables, mutatingScreenKind, [], (desc, index, descr, pdc) => ({
                kind: LocationKind.Component,
                screenName,
                tabIndex,
                componentIndexes: index,
                componentDisplayName: desc.builderDisplayName ?? descr.name,
                isWritten: doesPDCWrite(pdc),
            }));
        };

        if (screen.kind === ScreenDescriptionKind.Class) {
            if (mutatingScreenKind === undefined && screen.canEdit === true) {
                if (contextTableName !== undefined) {
                    this.addScreensToVisit([editClassScreenName(contextTableName)]);
                }

                // FIXME: Do we have a descriptor for this?
                this.visitArrayTransforms(screen.canEditFilters, contextTableName, undefined, undefined, () => ({
                    ...location,
                    propertySection: PropertySection.Edit,
                    isWritten: false,
                }));
            }
            if (mutatingScreenKind === MutatingScreenKind.EditScreen && screen.canDelete === true) {
                // FIXME: Do we have a descriptor for this?
                this.visitArrayTransforms(screen.canDeleteFilters, contextTableName, undefined, undefined, () => ({
                    ...location,
                    propertySection: PropertySection.Delete,
                    isWritten: false,
                }));
            }

            this.visitProperty(screen.title, undefined, undefined, tables, screen, mutatingScreenKind, pdc => ({
                ...location,
                propertySection: PropertySection.Title,
                isWritten: doesPDCWrite(pdc),
            }));

            // FIXME: use `searchPlaceholderPropertyHandler`
            // FIXME: In array screens, only visit if `desc.search` switch
            // property is true.  In class screens, only visit if it has a
            // searched inline list.
            this.visitProperty(
                screen.searchPlaceholder,
                undefined,
                undefined,
                tables,
                screen,
                mutatingScreenKind,
                pdc => ({
                    ...location,
                    propertySection: PropertySection.Search,
                    isWritten: doesPDCWrite(pdc),
                })
            );

            visitScreenComponents();

            if (mutatingScreenKind !== undefined && tables !== undefined) {
                const editScreen = screen as EditScreenDescription;
                const columnAssignments = getColumnAssignments(editScreen);
                this.visitColumnAssignments(
                    columnAssignments,
                    tables,
                    screen,
                    mutatingScreenKind,
                    (assignment, index) => ({
                        kind: LocationKind.ScreenColumnAssignment,
                        screenName,
                        tabIndex,
                        tableName: getTableName(defined(tables).output),
                        columnName: assignment.destColumn,
                        assignmentIndex: index,
                    })
                );

                for (const action of editScreen.onSubmitActions ?? []) {
                    this.visitAction(
                        action,
                        makeInputOutputTablesForFormOnSubmitAction(tables),
                        mutatingScreenKind,
                        pdc => ({
                            ...location,
                            propertySection: PropertySection.OnSubmitAction,
                            isWritten: doesPDCWrite(pdc),
                        })
                    );
                }
            }
        } else if (screen.kind === ScreenDescriptionKind.Array) {
            assert(mutatingScreenKind === undefined);

            if (getSwitchProperty(screen.canAddRow) === true) {
                // The filters are handled by the property descriptors.

                if (contextTableName !== undefined) {
                    this.addScreensToVisit([addClassScreenName(contextTableName)]);
                }
            }

            const screenHandler = handlerForArrayScreenFormat(screen.format);
            if (screenHandler === undefined) return;

            this.addScreensToVisit(screenHandler.getScreensUsed(screen, tables, false));

            if (screenHandler.hasComponents) {
                visitScreenComponents();
            }

            const descrs = screenHandler.getPropertyDescriptors(
                screen.type.kind === "table-ref",
                screen,
                isDefaultArrayScreenName(screenName),
                isFreeScreen(screenName, screen),
                ccc
            );
            this.visitProperties(screen, screen, descrs, tables, mutatingScreenKind, pdc => ({
                ...location,
                propertySection: undefined,
                isWritten: doesPDCWrite(pdc),
            }));

            const actionDescrs = screenHandler.getActionDescriptors(screen, undefined, ccc);
            this.visitActionsRecord(screenHandler.getActions(screen, ccc), tables, undefined, (pdc, name) => {
                const actionDescr = actionDescrs.find(
                    a => isNamedPropertySource(a.property) && a.property.name === name
                );
                return {
                    ...location,
                    propertySection:
                        typeof actionDescr?.section === "string" ? actionDescr.section : PropertySection.Action,
                    isWritten: doesPDCWrite(pdc),
                };
            });

            if (tables !== undefined) {
                const editedColumns = screenHandler.getEditedColumns(
                    undefined,
                    screen,
                    tables,
                    ccc,
                    mutatingScreenKind,
                    false,
                    isDefaultArrayScreenName(screenName),
                    false
                );
                for (const [cn, , tn] of editedColumns) {
                    this.visitor.visitColumnWritten?.(
                        { ...location, propertySection: undefined, isWritten: true },
                        tn,
                        cn
                    );
                }
            }
        } else {
            return assertNever(screen);
        }
    }

    public walkBuilderAction(actionID: string, root: CompoundActionNode, perAppTable: TableGlideType): void {
        const { ccc } = this;

        walkActionNode(root, n => {
            const location = {
                kind: LocationKind.Action,
                actionID,
                nodeKey: n.key,
                actionDisplayName: undefined,
            } as const;

            switch (n.kind) {
                case ActionNodeKind.Compound:
                case ActionNodeKind.Flow:
                    break;
                case ActionNodeKind.ConditionalFlow:
                    this.visitor.visitFormula?.(
                        { ...location, isWritten: false },
                        n.condition,
                        getTableName(perAppTable),
                        undefined,
                        undefined
                    );
                    break;
                case ActionNodeKind.Primitive:
                    const handler = handlerForActionKind(n.actionDescription.kind);
                    const descr = handler?.getDescriptor(n.actionDescription, undefined, ccc, undefined);
                    this.visitAction(
                        n.actionDescription,
                        definedMap(perAppTable, makeInputOutputTables),
                        undefined,
                        pdc => ({
                            ...location,
                            actionDisplayName: descr?.name,
                            isWritten: doesPDCWrite(pdc),
                        })
                    );
                    break;
                default:
                    return assertNever(n);
            }
            return n;
        });
    }

    private visitBuilderAction(actionID: string, table: TableGlideType | undefined): void {
        const { ccc } = this;

        const builderAction = ccc.getBuilderAction(actionID);
        if (builderAction === undefined) return;

        if (table === undefined) {
            const perApp = builderAction.perApp[ccc.appID];
            table = ccc.findTable(perApp?.tableName);
            if (table === undefined) return;
        }

        this.walkBuilderAction(actionID, builderAction.action, table);
    }

    public walkAppDescription(
        includeHiddenTabs: boolean,
        includeDefaultScreens: boolean
    ): { screensVisited: ReadonlySet<string> } {
        const { ccc, app } = this;

        if (app.userProfile !== undefined) {
            this.visitProperties(
                app.userProfile,
                app.userProfile,
                getUserProfileDescriptors(ccc.eminenceFlags),
                undefined,
                undefined,
                (pdc, pd) => ({
                    kind: LocationKind.UserProfile,
                    name: (typeof pd?.label === "function" ? pd.label(undefined) : pd?.label) ?? "Property",
                    isWritten: doesPDCWrite(pdc),
                })
            );
        }

        this.addScreensToVisit([userProfileScreenName]);

        const tabs = getAppTabs(app);
        for (const [index, tab] of iterableEnumerate(tabs)) {
            if (tab.hidden && !includeHiddenTabs) continue;

            const screenName = getScreenProperty(tab.screenName);
            if (screenName !== undefined) {
                this.addScreensToVisit([screenName]);
            }

            const location: Location = { kind: LocationKind.Tab, index };
            // this.visitor.visitTabDescription?.(tab);
            // FIXME: Do we have a descriptor for this?
            this.visitArrayTransforms(tab.visibilityFilters, undefined, undefined, undefined, () => location);
        }

        if (includeDefaultScreens) {
            for (const table of ccc.schema.tables) {
                const tableRef = makeTableRef(table);
                this.addScreensToVisit([arrayScreenName(tableRef)]);
                this.addScreensToVisit([classScreenName(tableRef)]);
                this.addScreensToVisit([addClassScreenName(tableRef)]);
                this.addScreensToVisit([editClassScreenName(tableRef)]);
            }
        }

        const screensVisited = new Set<string>();
        const builderActionsVisited = new Set<string>();
        for (;;) {
            const screenName = this.screensToVisit.shift();
            if (screenName !== undefined) {
                if (screensVisited.has(screenName)) continue;
                screensVisited.add(screenName);

                this.visitScreen(screenName);
                continue;
            }

            const toVisit = this.builderActionsToVisit.shift();
            if (toVisit !== undefined) {
                const [actionID, table] = toVisit;

                if (builderActionsVisited.has(actionID)) continue;
                builderActionsVisited.add(actionID);

                this.visitBuilderAction(actionID, table);
                continue;
            }

            break;
        }

        return { screensVisited };
    }
}

export function walkAppDescription(
    ccc: ComponentConfiguratorContext,
    visitor: AppDescriptionVisitor,
    includeHiddenTabs: boolean,
    includeDefaultScreens: boolean
): void {
    if (ccc.appDescription === undefined) return;

    const walker = new AppWalker(ccc, visitor);
    walker.walkAppDescription(includeHiddenTabs, includeDefaultScreens);
}

export function walkBuilderAction(
    ccc: ComponentConfiguratorContext,
    visitor: AppDescriptionVisitor,
    actionID: string,
    root: CompoundActionNode,
    table: TableGlideType
): void {
    if (ccc.appDescription === undefined) return;

    const walker = new AppWalker(ccc, visitor);
    walker.walkBuilderAction(actionID, root, table);
}
